import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Dropdown, Layout, Segmented } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightFromBracket,
  faBookOpen,
  faCircleInfo,
  faLanguage,
  faUserGear,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../infrastructure/auth0/useAuth';
import { LANGUAGE_LABELS } from '../internationalization/language';
import { useLanguage } from '../hooks/useLanguage';
import { useInstructionsVisibility } from '../hooks/SimViewer/useInstructionsVisibility';
import { Logo } from '../components/Logo';

type Props = {
  items?: {
    left?: ReactNode;
    right?: ReactNode;
  };
};

const MANUAL_PAGE_URL =
  'https://automatic-side-ab6.notion.site/GEOTRA-Web-10663e02d0a7463784f93b2bcc665016';

const Header = ({ items }: Props): ReactNode => {
  const { currentUser, logout } = useAuth();
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const { resetInstructionsVisibility } = useInstructionsVisibility();

  return (
    <StyledHeader>
      <HeaderItem>
        <StyledLink to="/">
          <Logo variant="square" width="auto" height={36} />
        </StyledLink>
        {items?.left}
      </HeaderItem>
      <HeaderItem>
        {items?.right}
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'user',
                type: 'group',
                label: `${currentUser?.userName ?? ''} (${currentUser?.organizationDisplayName ?? ''})`,
                children: [
                  {
                    key: 'language',
                    label: (
                      <RowContainer>
                        <span>{t('language')}</span>
                        <Segmented
                          size="small"
                          options={Object.entries(LANGUAGE_LABELS).map(
                            ([key, value]: [string, string]) => {
                              return {
                                label: value,
                                value: key,
                              };
                            }
                          )}
                          value={language}
                          style={{
                            padding: '1px 2px',
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                          }}
                          onChange={(value: string | number): void => {
                            if (value === 'ja' || value === 'en') {
                              changeLanguage(value);
                            }
                          }}
                        />
                      </RowContainer>
                    ),
                    icon: <FontAwesomeIcon icon={faLanguage} />,
                    style: { cursor: 'auto', pointerEvents: 'none' },
                  },
                  {
                    key: 'instructions',
                    label: (
                      <RowContainer>
                        <span>{t('instructions')}</span>
                        <Button
                          type="ghost"
                          size="small"
                          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                          onClick={(): void => {
                            resetInstructionsVisibility();
                          }}
                        >
                          {t('showAgain')}
                        </Button>
                      </RowContainer>
                    ),
                    icon: <FontAwesomeIcon icon={faCircleInfo} />,
                    style: { cursor: 'auto', pointerEvents: 'none' },
                  },
                  {
                    key: 'manual',
                    label: (
                      <a
                        href={MANUAL_PAGE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('manual')}
                      </a>
                    ),
                    icon: <FontAwesomeIcon icon={faBookOpen} />,
                  },
                  currentUser?.isAdmin
                    ? {
                        key: 'manageOrg',
                        label: <Link to="/organization">{t('manageOrg')}</Link>,
                        icon: <FontAwesomeIcon icon={faUserGear} />,
                      }
                    : null,
                  {
                    key: 'logout',
                    label: t('logout'),
                    icon: <FontAwesomeIcon icon={faArrowRightFromBracket} />,
                    onClick: () =>
                      logout({
                        logoutParams: { returnTo: window.location.origin },
                      }),
                  },
                ],
              },
            ],
          }}
        >
          <Button type="text" style={{ alignItems: 'center', display: 'flex' }}>
            <UserOutlined style={{ fontSize: '16px', height: '16px' }} />
          </Button>
        </Dropdown>
      </HeaderItem>
    </StyledHeader>
  );
};

const StyledHeader = styled(Layout.Header)`
  width: 100%;
  height: 48px;
  padding: 0px 18px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: #262626;
  color: #f9f9f9;

  .ant-btn-text {
    color: #f9f9f9;
  }

  .ant-btn-text:hover,
  .ant-btn-text:focus {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
`;

const HeaderItem = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export { Header };
